import { ReactNode, useMemo, useState } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  ListButton,
  Pagination,
  ReferenceField,
  ReferenceManyField,
  Show,
  ShowButton,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { SharePointUrlField, formatCampaignTitle } from '../campaigns/CampaignList';
import { AdvertiserRecord, ArchiveAdvertiserButton, advertiserTypeChoices } from './shared';

type CampaignSummary = {
  id?: string;
  name?: string | null;
  campaignNumber?: number | null;
  gamOrderId?: string | null;
  dcmAdvertiserId?: string | null;
  sharepointContractUrl?: string | null;
};

const formatChoice = (choices: { id: string; name: string }[], value?: string | null) =>
  choices.find(choice => choice.id === value)?.name ?? value ?? '-';

const sectionGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  gap: 2,
} as const;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box minWidth={0}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Box sx={{ '& .MuiTypography-root': { fontSize: 'body2.fontSize' } }}>{children}</Box>
  </Box>
);

const AdvertiserShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
    <ArchiveAdvertiserButton />
  </TopToolbar>
);

const AdvertiserHeader = () => {
  const record = useRecordContext<AdvertiserRecord>();
  if (!record) return null;
  const typeLabel = formatChoice(advertiserTypeChoices, record.type);
  return (
    <CardContent>
      <CardHeader title={record.name} subheader={typeLabel} sx={{ px: 0, pt: 0, pb: 1 }} />
    </CardContent>
  );
};

const OverviewSection = () => (
  <CardContent>
    <CardHeader title="Overview" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Name">
        <TextField source="name" emptyText="-" />
      </Field>
      <Field label="Type">
        <FunctionField
          source="type"
          render={(record: AdvertiserRecord) => formatChoice(advertiserTypeChoices, record?.type)}
        />
      </Field>
      <Field label="Agency">
        <ReferenceField source="parentId" reference="advertisers" link="show">
          <TextField source="name" emptyText="-" />
        </ReferenceField>
      </Field>
      <Field label="GAM/DCM Company ID">
        <TextField source="adServerId" emptyText="-" />
      </Field>
      <Field label="External PIO ID">
        <TextField source="externalPioId" emptyText="-" />
      </Field>
      <Field label="Advertiser ID">
        <TextField source="id" emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const AuditSection = () => (
  <CardContent>
    <CardHeader title="Audit" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Created">
        <ESTDateField source="createdAt" showTime />
      </Field>
      <Field label="Updated">
        <ESTDateField source="updatedAt" showTime />
      </Field>
      <Field label="Archived">
        <ESTDateField source="archivedAt" showTime emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const CampaignsToolbar = ({
  filterValue,
  onFilterChange,
}: {
  filterValue: string;
  onFilterChange: (value: string) => void;
}) => {
  const record = useRecordContext<AdvertiserRecord>();

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
      <MuiTextField
        label="Filter campaigns"
        value={filterValue}
        onChange={event => onFilterChange(event.target.value)}
        size="small"
        sx={{ minWidth: 260 }}
      />
      <CreateButton
        resource="campaigns"
        label="Add campaign"
        to={{
          pathname: '/campaigns/create',
          search: record?.id ? `?advertiserId=${record.id}` : undefined,
        }}
      />
    </Box>
  );
};

const CampaignsTable = () => {
  const [filterValue, setFilterValue] = useState('');
  const campaignFilter = useMemo(() => (filterValue ? { name: filterValue } : {}), [filterValue]);

  return (
    <CardContent>
      <CardHeader title="Campaigns for This Advertiser" sx={{ px: 0, pt: 0 }} />
      <CampaignsToolbar filterValue={filterValue} onFilterChange={setFilterValue} />
      <ReferenceManyField
        reference="campaigns"
        target="advertiserId"
        sort={{ field: 'startDate', order: 'DESC' }}
        perPage={25}
        filter={campaignFilter}
        pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
      >
        <Datagrid
          rowClick="show"
          bulkActionButtons={false}
          empty={<Box sx={{ p: 2 }}>No campaigns for this advertiser.</Box>}
        >
          <FunctionField
            label="Campaign"
            source="name"
            render={(record: CampaignSummary) => formatCampaignTitle(record)}
          />
          <ESTDateField source="startDate" label="Start Date" textAlign="center" />
          <ESTDateField source="endDate" label="End Date" textAlign="center" />
          <TextField source="gamOrderId" label="GAM Order ID" emptyText="-" />
          <TextField source="dcmAdvertiserId" label="DCM Advertiser ID" emptyText="-" />
          <SharePointUrlField />
          <ShowButton resource="campaigns" />
          <EditButton resource="campaigns" />
        </Datagrid>
      </ReferenceManyField>
    </CardContent>
  );
};

export const AdvertiserShow = () => (
  <Show actions={<AdvertiserShowActions />}>
    <AdvertiserHeader />
    <Divider />
    <OverviewSection />
    <Divider />
    <AuditSection />
    <Divider />
    <CampaignsTable />
  </Show>
);
